import { useEffect, useState } from "react";
import {
  BarChart3,
  Download,
  LayoutList,
  RefreshCw,
  Search,
  Table2,
  Users,
  X,
} from "lucide-react";
import { formApi, type FormResponse, type FormSummary } from "./api";
import { formatAnswer } from "./FormExperience";
import type { FeedbackForm } from "./contract";
import { useDebounce } from "../../hooks/useDebounce";
export function Responses({ form }: { form: FeedbackForm }) {
  const [mode, setMode] = useState<"summary" | "individual" | "table">(
      "summary",
    ),
    [summary, setSummary] = useState<FormSummary | null>(null),
    [rows, setRows] = useState<FormResponse[]>([]),
    [total, setTotal] = useState(0),
    [query, setQuery] = useState(""),
    [page, setPage] = useState(0),
    [refresh, setRefresh] = useState(0),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [exporting, setExporting] = useState(false),
    [selected, setSelected] = useState<FormResponse | null>(null);
  const search = useDebounce(query, 250);
  useEffect(() => {
    if (selected)
      document
        .querySelector("[data-response-detail]")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selected]);
  useEffect(() => {
    let stopped = false;
    setLoading(true);
    Promise.all([
      formApi.summary(form.id),
      formApi.responses(form.id, search, page),
    ])
      .then(([stats, result]) => {
        if (!stopped) {
          setSummary(stats);
          setRows(result.responses);
          setTotal(result.total);
          setError("");
        }
      })
      .catch((e) => {
        if (!stopped) setError(e.message);
      })
      .finally(() => {
        if (!stopped) setLoading(false);
      });
    return () => {
      stopped = true;
    };
  }, [form.id, search, page, refresh]);
  const questions = form.questions.filter((q) => q.type !== "section");
  function inspect(value: string) {
    setQuery(value);
    setPage(0);
    setMode("individual");
  }
  async function download() {
    setExporting(true);
    try {
      await formApi.download(form.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  }
  return (
    <div className="space-y-5" data-tour="forms-responses">
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <div className="tabs" aria-label="Response views">
          {(
            [
              { id: "summary", label: "Overview", icon: BarChart3 },
              { id: "individual", label: "People", icon: Users },
              { id: "table", label: "Table", icon: Table2 },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              className="tab"
              data-active={mode === tab.id ? "true" : "false"}
              onClick={() => setMode(tab.id)}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            className="glass-button-secondary"
            onClick={() => setRefresh(refresh + 1)}
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            className="btn-primary"
            onClick={() => void download()}
            disabled={exporting || !summary?.total}
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>
      {error && (
        <p role="alert" className="text-red-400 text-sm">
          {error}
        </p>
      )}
      {loading && !summary ? (
        <p role="status" className="text-glass-400 py-10 text-center">
          Gathering the responses…
        </p>
      ) : !summary?.total ? (
        <div className="glass-card p-12 text-center">
          <LayoutList className="w-8 h-8 mx-auto mb-5 text-glass-400" />
          <h2 className="font-medium">The conversation starts with a share.</h2>
          <p className="text-sm text-glass-400 mt-2">
            Publish your form, then share its Hub link. Responses will appear
            here.
          </p>
        </div>
      ) : (
        <>
          {mode === "summary" ? (
            <>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="glass-card p-5">
                  <p className="text-xs text-glass-400">Responses</p>
                  <p className="text-3xl font-semibold mt-2">{summary.total}</p>
                  <p className="text-xs text-glass-400 mt-2">
                    One response per linked player
                  </p>
                </div>
                <div className="glass-card p-5">
                  <p className="text-xs text-glass-400">Questions</p>
                  <p className="text-3xl font-semibold mt-2">
                    {questions.length}
                  </p>
                  <p className="text-xs text-glass-400 mt-2">
                    {questions.filter((q) => q.required).length} required
                  </p>
                </div>
                <div className="glass-card p-5">
                  <p className="text-xs text-glass-400">Latest response</p>
                  <p className="text-xl font-semibold mt-3">
                    {summary.daily[summary.daily.length - 1]?.day}
                  </p>
                  <p className="text-xs text-glass-400 mt-2">
                    Dates shown in UTC
                  </p>
                </div>
              </div>
              <section className="glass-card p-5">
                <h2 className="text-sm font-medium mb-5">
                  The conversation over time
                </h2>
                <div
                  className="flex h-28 items-end gap-1"
                  aria-label="Responses by day"
                >
                  {summary.daily.slice(-60).map((day) => (
                    <div
                      key={day.day}
                      className="flex-1 bg-accent-500/40 hover:bg-accent-500/70 rounded-t min-w-0"
                      style={{
                        height: `${Math.max(3, (day.count / Math.max(...summary.daily.map((x) => x.count))) * 100)}%`,
                      }}
                      title={`${day.day}: ${day.count} responses`}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-glass-400 mt-2">
                  <span>{summary.daily.slice(-60)[0]?.day}</span>
                  <span>{summary.daily[summary.daily.length - 1]?.day}</span>
                </div>
                <details className="text-xs text-glass-400 mt-4">
                  <summary className="cursor-pointer">
                    View daily counts
                  </summary>
                  <div className="max-h-40 overflow-auto mt-3">
                    {summary.daily.map((day) => (
                      <p key={day.day}>
                        {day.day}: {day.count} response
                        {day.count !== 1 ? "s" : ""}
                      </p>
                    ))}
                  </div>
                </details>
              </section>
              <div className="grid lg:grid-cols-2 gap-4">
                {questions.map((q, index) => {
                  const values = summary.distribution.filter(
                    (d) => d.question_id === q.id,
                  );
                  const mean =
                    summary.numbers.find((value) => value.question_id === q.id)
                      ?.mean ?? null;
                  return (
                    <section className="glass-card p-5 min-w-0" key={q.id}>
                      <p className="eyebrow mb-2">Question {index + 1}</p>
                      <h3 className="font-medium text-sm mb-4">{q.title}</h3>
                      {mean !== null && (
                        <p className="text-2xl font-semibold mb-5">
                          {mean.toFixed(1)}
                          <span className="text-xs text-glass-400 font-normal ml-2">
                            average · range {q.min}–{q.max}
                          </span>
                        </p>
                      )}
                      {values.length ? (
                        <div className="space-y-3">
                          {values.slice(0, 40).map((v, i) => (
                            <button
                              key={i}
                              className="w-full text-left"
                              title="Find responses with this answer"
                              onClick={() => inspect(String(v.value))}
                            >
                              <span className="flex justify-between gap-3 text-xs mb-1.5">
                                <span className="truncate">
                                  {v.row_label ? `${v.row_label}: ` : ""}
                                  {String(v.value)}
                                </span>
                                <span className="text-glass-400 shrink-0">
                                  {v.count} ·{" "}
                                  {Math.round((v.count / summary.total) * 100)}%
                                </span>
                              </span>
                              <span className="block h-2 rounded bg-glass-200/10">
                                <span
                                  className="block h-2 rounded bg-accent-500/60"
                                  style={{
                                    width: `${(v.count / summary.total) * 100}%`,
                                  }}
                                />
                              </span>
                            </button>
                          ))}
                          {values.length > 40 && (
                            <p className="text-xs text-glass-400">
                              Top 40 values shown. Export for all values.
                            </p>
                          )}
                          <p className="text-[10px] text-glass-400">
                            Percentage of all responses. Multiple-choice totals
                            can exceed 100%.
                          </p>
                        </div>
                      ) : (
                        <>
                          <p className="text-xs text-glass-400 leading-relaxed">
                            {["short", "paragraph", "date", "time"].includes(
                              q.type,
                            )
                              ? "Every answer tells a story. Read responses in context or explore them in the table."
                              : "No answers to this question yet."}
                          </p>
                          <button
                            className="glass-button-secondary mt-4"
                            onClick={() => {
                              setQuery("");
                              setMode("table");
                            }}
                          >
                            Read answers
                          </button>
                        </>
                      )}
                    </section>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-3 items-center">
                <label className="relative flex-1">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-glass-400" />
                  <input
                    className="glass-input pl-9 w-full"
                    aria-label="Search responses"
                    placeholder="Search players or answers…"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setPage(0);
                    }}
                  />
                </label>
                <span className="text-xs text-glass-400">{total} found</span>
              </div>
              {mode === "individual" ? (
                <div className="grid md:grid-cols-2 gap-3">
                  {rows.map((row) => (
                    <button
                      className="glass-card glass-card-interactive p-5 text-left"
                      key={row.id}
                      onClick={() => setSelected(row)}
                    >
                      <h3 className="font-medium text-sm">
                        {row.display_name}
                      </h3>
                      <p className="text-xs text-glass-400 mt-1">
                        {new Date(row.created_at).toLocaleString()}
                      </p>
                      <p className="text-sm mt-4 line-clamp-2">
                        {formatAnswer(Object.values(row.answers)[0])}
                      </p>
                      <span className="text-xs text-accent-300 mt-4 block">
                        Read full response →
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="glass-card overflow-auto max-h-[65vh]">
                  <table className="w-full text-xs text-left">
                    <thead className="sticky top-0 bg-[var(--glass-bg)]">
                      <tr>
                        <th className="p-3 min-w-36">Player</th>
                        <th className="p-3 min-w-40">Submitted</th>
                        {questions.map((q) => (
                          <th className="p-3 min-w-52 max-w-72" key={q.id}>
                            {q.title}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr
                          className="border-t border-glass-200/10"
                          key={row.id}
                        >
                          <td className="p-3">
                            <button
                              className="text-accent-300"
                              onClick={() => setSelected(row)}
                            >
                              {row.display_name}
                            </button>
                          </td>
                          <td className="p-3 text-glass-400">
                            {new Date(row.created_at).toLocaleString()}
                          </td>
                          {questions.map((q) => (
                            <td
                              className="p-3 max-w-80 whitespace-pre-wrap break-words"
                              key={q.id}
                            >
                              {formatAnswer(row.answers[q.id]) || "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!rows.length && (
                <p className="text-glass-400 py-8 text-center">
                  No responses match this search.
                </p>
              )}
              <div className="flex justify-end gap-3 items-center text-xs">
                <button
                  className="glass-button-secondary"
                  disabled={!page || loading}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {page + 1} of {Math.max(1, Math.ceil(total / 50))}
                </span>
                <button
                  className="glass-button-secondary"
                  disabled={(page + 1) * 50 >= total || loading}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </>
      )}
      {selected && (
        <div
          className="glass-card p-5 space-y-5"
          data-response-detail
          role="region"
          aria-label={`Response from ${selected.display_name}`}
        >
          <div className="flex justify-between">
            <div>
              <h2 className="font-medium">{selected.display_name}</h2>
              <p className="text-xs text-glass-400 mt-1">
                {new Date(selected.created_at).toLocaleString()} ·{" "}
                <a
                  href={`/player/${encodeURIComponent(selected.member_uuid)}`}
                  className="text-accent-300"
                >
                  Player profile
                </a>
              </p>
            </div>
            <button
              className="btn-ghost"
              aria-label="Close response"
              onClick={() => setSelected(null)}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {questions.map((q) => (
            <div className="border-t border-glass-200/10 pt-4" key={q.id}>
              <h3 className="text-sm font-medium">{q.title}</h3>
              <p className="text-sm text-glass-400 mt-2 whitespace-pre-wrap break-words">
                {formatAnswer(selected.answers[q.id]) ||
                  "Not answered / not shown"}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
